/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import { store } from 'redux/store';

import transactionTypes from 'constants/transactionTypes';
import { Movement, Range, Transaction } from 'types';

import { generateEmail } from 'utils/filters';
import normalizeCif from 'utils/normalizeCif';

import { addPartners } from 'modules/_shared/redux/modalsActions';
import movementTypes from 'modules/transactions/constants/movementTypes';
import { createMovement } from 'modules/transactions/utils/createMovement';
import { createTransaction } from 'modules/transactions/utils/createTransaction';
import { getPartnerAvailableRanges } from 'modules/partners/api/getPartnerAvailableRanges';
import { isValidRangeForPartner } from 'modules/partners/api/isValidRangeForPartner';

import { getSharePrice } from 'modules/_shared/components/Modals/SharesTransfer/utils/getSharePrice';
import priceTypes from 'modules/_shared/components/Modals/SharesTransfer/constants/priceTypes';

import { buildSellerTransferMovements } from './buildSellerTransferMovements';
import { findContainingSeller } from './groupSharesTransferExcelByOperation';
import {
  SharesTransferExcelOperation,
  SharesTransferExcelRow,
} from '../types/SharesTransferExcel';

type Society = {
  _id: string;
  name: string;
  currentPartner?: number;
  sharesCount?: { actual?: number };
};

type User = {
  _id: string;
  email: string;
};

const getUnitSharePrice = (
  row: SharesTransferExcelRow,
  fallbackRow: SharesTransferExcelRow | null,
  societySharesCount?: number
): number => {
  const totalPrice = row.sharePrice ?? fallbackRow?.sharePrice ?? 0;
  const sharesCount = Math.abs(row.totalShares);

  return getSharePrice({
    price: totalPrice,
    priceType: priceTypes[0].value,
    sharesToTransfer: sharesCount,
    societySharesCount: societySharesCount || 0,
  });
};

const fetchPartnerAvailableRanges = async ({
  partnerSellerId,
  holdingClassId,
}: {
  partnerSellerId: string;
  holdingClassId: string;
}): Promise<Range[]> => {
  const rangesData = (await store.dispatch(
    getPartnerAvailableRanges({
      partnerId: partnerSellerId,
      holdingClassId,
      params: { page: 1, items: 100000 },
    })
  )) as { ranges?: Range[] } | undefined;

  return rangesData?.ranges || [];
};

const buildSellerMovements = async ({
  seller,
  partnerSellerId,
  holdingClassId,
}: {
  seller: SharesTransferExcelRow;
  partnerSellerId: string;
  holdingClassId: string;
}): Promise<Movement[]> => {
  const container = (await store.dispatch(
    isValidRangeForPartner({
      partnerId: partnerSellerId,
      holdingClassId,
      shareFrom: seller.shareFrom,
      shareTo: seller.shareTo,
    })
  )) as Range | undefined;

  if (container && container.from !== -1) {
    return buildSellerTransferMovements({
      partnerSellerId,
      holdingClassId,
      shareFrom: seller.shareFrom,
      shareTo: seller.shareTo,
      availableRanges: [container],
    });
  }

  const availableRanges = await fetchPartnerAvailableRanges({
    partnerSellerId,
    holdingClassId,
  });

  const fragmentMovements = buildSellerTransferMovements({
    partnerSellerId,
    holdingClassId,
    shareFrom: seller.shareFrom,
    shareTo: seller.shareTo,
    availableRanges,
  });

  if (!fragmentMovements.length) {
    throw new Error(
      `RANGE_NOT_AVAILABLE:${seller.cif}:${seller.shareFrom}-${seller.shareTo}`
    );
  }

  return fragmentMovements;
};

export const mapSharesTransferOperationToPayload = async ({
  operation,
  partnerIdByCif,
  holdingClassId,
  society,
}: {
  operation: SharesTransferExcelOperation;
  partnerIdByCif: Record<string, string>;
  holdingClassId: string;
  society: Society;
}): Promise<{
  movements: Movement[];
  transactions: Transaction[];
  operationType: string;
  comments: string;
  date: Date;
}> => {
  const movements: Movement[] = [];
  const transactions: Transaction[] = [];
  const processedSellerKeys = new Set<string>();

  for (const seller of operation.sellers) {
    const sellerKey = `${seller.cif}:${seller.shareFrom}:${seller.shareTo}`;

    if (!processedSellerKeys.has(sellerKey)) {
      processedSellerKeys.add(sellerKey);

      const partnerSellerId = partnerIdByCif[normalizeCif(seller.cif)];
      if (!partnerSellerId) {
        throw new Error(`Missing seller partner ID for CIF ${seller.cif}`);
      }

      const sellerMovements = await buildSellerMovements({
        seller,
        partnerSellerId,
        holdingClassId,
      });

      movements.push(...sellerMovements);
    }
  }

  for (const buyer of operation.buyers) {
    const partnerBuyerId = partnerIdByCif[normalizeCif(buyer.cif)];
    if (!partnerBuyerId) {
      throw new Error(`Missing buyer partner ID for CIF ${buyer.cif}`);
    }

    const seller = findContainingSeller(operation.sellers, buyer);
    const partnerSellerId = seller
      ? partnerIdByCif[normalizeCif(seller.cif)]
      : '';

    if (!partnerSellerId) {
      throw new Error(`Missing seller for buyer CIF ${buyer.cif}`);
    }

    const buyerRange: Range = {
      from: buyer.shareFrom,
      to: buyer.shareTo,
      partner: partnerBuyerId,
      shareClass: holdingClassId,
    };

    movements.push(
      createMovement({
        range: buyerRange,
        movementType: movementTypes.INCREMENT,
      }) as Movement
    );

    const sharePrice = getUnitSharePrice(
      buyer,
      seller,
      society.sharesCount?.actual
    );

    const transactionRange: Range = {
      ...buyerRange,
      sharePrice,
    };

    transactions.push(
      createTransaction({
        range: {
          ...transactionRange,
          partner: partnerSellerId,
        },
        transactionType: transactionTypes.SELL,
      }) as Transaction,
      createTransaction({
        range: transactionRange,
        transactionType: transactionTypes.BUY,
      }) as Transaction
    );
  }

  const operationType =
    operation.selectedOperationType || operation.operationTypes[0];

  return {
    movements,
    transactions,
    operationType,
    comments: operation.rows[0]?.comments || operation.operationId,
    date: operation.date,
  };
};

export const resolveSharesTransferPartnersFromExcel = async ({
  rows,
  society,
  user,
  operationDate,
  existingPartners,
}: {
  rows: SharesTransferExcelRow[];
  society: Society;
  user: User;
  operationDate: Date;
  existingPartners: { _id: string; cif: string }[];
}): Promise<Record<string, string>> => {
  const partnerIdByCif: Record<string, string> = {};

  existingPartners.forEach((partner) => {
    if (partner.cif) {
      partnerIdByCif[partner.cif] = partner._id;
    }
  });

  const uniqueCifs = [...new Set(rows.map((row) => normalizeCif(row.cif)))];
  const uniqueRows = uniqueCifs.map(
    (cif) => rows.find((row) => normalizeCif(row.cif) === cif)!
  );

  const rowsToCreate = uniqueRows.filter((row) => !partnerIdByCif[row.cif]);

  if (rowsToCreate.length) {
    const formattedPartners = rowsToCreate.map((row, index) => ({
      society: society._id,
      date: operationDate,
      user: user._id,
      societyName: society.name,
      userEmail: user.email,
      source: 'UPLOAD',
      email:
        row.email ||
        generateEmail(society, (society.currentPartner || 0) + index + 1),
      cif: row.cif,
      name: row.name,
    }));

    const uploadedPartners = await store.dispatch(
      addPartners(formattedPartners)
    );

    uploadedPartners?.forEach((partner: { _id: string; cif?: string }) => {
      const cif = normalizeCif(partner?.cif || '');
      if (cif) {
        partnerIdByCif[cif] = partner._id;
      }
    });
  }

  const missingCifs = uniqueCifs.filter((cif) => !partnerIdByCif[cif]);

  if (missingCifs.length) {
    throw new Error(`Missing partner IDs for CIFs: ${missingCifs.join(', ')}`);
  }

  return partnerIdByCif;
};
